<template>
  <div>
    <div v-if="nonNullValues > 1" class="sparkline-container" @click="showExpandedChart">
      <canvas ref="sparkline" width="50" height="20" />
      <span
        v-if="diffPerc !== null"
        :class="{
          'text-positive': higherIsBetter ? diffPerc >= 0 : diffPerc < 0,
          'text-negative': higherIsBetter ? diffPerc < 0 : diffPerc >= 0
        }"
      >
        {{ formatPercent(diffPerc, 1, true) }}
        <q-tooltip v-if="first !== null && last !== null">
          {{ label !== null ? label + ' has' : 'Has' }} changed by
          {{ formatPercent(diffPerc, 1, true) }} from {{ formatMoney(first.value, 2) }} to
          {{ formatMoney(last.value, 2) }}
          between {{ formatDateString(first.date) }} and {{ formatDateString(last.date) }}
        </q-tooltip>
      </span>
    </div>
    <div v-else class="sparkline-container">
      <div
        :class="$q.dark.isActive ? 'bg-grey-9' : 'bg-grey-3'"
        style="width: 50px; height: 20px"
      />
      <span class="text-grey text-caption q-mr-md">--</span>
    </div>

    <!-- Expanded Chart Modal -->
    <q-dialog v-model="expandedChartVisible" transition-duration="100">
      <q-card style="width: 700px; max-width: 90vw">
        <q-card-section class="row items-center">
          <div class="text-h6">{{ label || 'Chart Details' }}</div>
          <q-space />
          <q-btn icon="close" flat round dense v-close-popup />
        </q-card-section>

        <q-separator />

        <q-card-section>
          <div style="height: 300px">
            <canvas ref="expandedChart" />
          </div>
        </q-card-section>
        <q-separator />
        <q-card-section v-if="first !== null && last !== null" class="flex items-center">
          <q-icon name="info" size="xs" class="q-mr-md" />
          <div>
            <div class="text-bold">Summary</div>
            <div :class="$q.dark.isActive ? 'text-grey-6' : 'text-grey-7'">
              {{ label !== null ? label + ' has' : 'Value has' }} changed by
              <b>
                <span
                  :class="{
                    'text-positive': higherIsBetter ? diffPerc >= 0 : diffPerc < 0,
                    'text-negative': higherIsBetter ? diffPerc < 0 : diffPerc >= 0
                  }"
                >
                  {{ formatPercent(diffPerc, 1, true) }}
                </span>
              </b>
              from
              <b>{{
                first.value > 100 ? formatMoney(first.value, 0) : formatMoney(first.value, 2)
              }}</b>
              to
              <b>{{
                first.value > 100 ? formatMoney(last.value, 0) : formatMoney(last.value, 2)
              }}</b>
              between <b>{{ formatDateString(first.date) }}</b> and
              <b>{{ formatDateString(last.date) }}</b>
            </div>
          </div>
        </q-card-section>
      </q-card>
    </q-dialog>
  </div>
</template>

<script lang="ts">
import { Chart, type ChartArea, type DefaultDataPoint } from 'chart.js'
import { formatMoney, formatPercent } from '@/utils'
import { format } from 'date-fns'
import type { ChartConfiguration } from 'chart.js/dist/types'

interface Datapoint {
  value: number | null
  date: string
}

interface NonNullDatapoint extends Datapoint {
  value: number
  date: string
}

function isNonNullDatapoint(val: Datapoint): val is NonNullDatapoint {
  return val.value !== null
}

export default {
  name: 'ChartSparkline',
  components: {},
  props: {
    values: {
      required: true,
      type: Array as () => Datapoint[],
      description:
        'An array of datapoints, each datapoint is an object with a value and a date. Assuming the array is sorted by date ascending.'
    },
    label: {
      required: false,
      type: String as () => string | null,
      description: 'The label to display on the left of the chart',
      default: null
    },
    relative: {
      required: false,
      type: Boolean as () => boolean,
      description: 'Whether to display the relative change instead of the absolute change',
      default: true
    },
    higherIsBetter: {
      required: false,
      type: Boolean as () => boolean,
      description: 'Whether a higher value is better or not',
      default: true
    }
  },
  data() {
    return {
      chart: null as Chart<'line'> | null,
      expandedChart: null as Chart<'line'> | null,
      expandedChartVisible: false
    }
  },
  computed: {
    diffPerc(): number | null {
      const firstNonNull = this.first
      const lastNonNull = this.last

      if (!firstNonNull || !lastNonNull) {
        return null
      }

      const diff = lastNonNull.value - firstNonNull.value
      if (this.relative) {
        return diff / firstNonNull.value
      }
      return diff
    },
    first(): NonNullDatapoint | null {
      if (this.nonNullValues < 1) {
        return null
      }
      return this.values.find(isNonNullDatapoint) ?? null
    },
    last(): NonNullDatapoint | null {
      if (this.nonNullValues < 1) {
        return null
      }
      return this.values.findLast(isNonNullDatapoint) ?? null
    },
    nonNullValues(): number {
      return this.values.filter(isNonNullDatapoint).length
    }
  },
  watch: {
    values() {
      this.rerender()
    },
    '$q.dark.isActive'() {
      this.rerender()
    }
  },
  mounted() {
    this.rerender()
  },
  methods: {
    clearChart() {
      this.chart?.destroy()
      this.chart = null
      this.expandedChart?.destroy()
      this.expandedChart = null
    },
    rerender() {
      if (this.nonNullValues > 1) {
        this.renderChart()
      } else {
        this.clearChart()
      }
    },
    formatPercent,
    formatMoney,
    formatDateString(dateStr: string | null) {
      if (dateStr) {
        const date = new Date(dateStr)
        return format(date, 'M/d/yyyy')
      }
      return ''
    },
    showExpandedChart() {
      if (this.nonNullValues <= 1) return

      this.expandedChartVisible = true

      this.$tracker.expandedTrendSparkline()

      // Wait for the DOM to update and the canvas to be available
      this.$nextTick(() => {
        this.renderExpandedChart()
      })
    },
    calculateAverage() {
      const avgObj = this.values.reduce(
        (acc, val) => {
          if (val.value === null) {
            return acc
          }
          acc.count += 1
          acc.sum += val.value
          return acc
        },
        { count: 0, sum: 0 }
      )
      return avgObj.count > 0 ? avgObj.sum / avgObj.count : null
    },

    createGradient(ctx: CanvasRenderingContext2D, chartArea: ChartArea) {
      const gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top)
      gradient.addColorStop(1, 'rgba(99, 101, 241, 0.5)')
      gradient.addColorStop(0, 'rgba(99, 101, 241, 0)')
      return gradient
    },

    renderExpandedChart() {
      const ctx = this.$refs.expandedChart as HTMLCanvasElement
      if (!ctx) {
        return
      }

      // Destroy previous chart if it exists
      if (this.expandedChart) {
        this.expandedChart.destroy()
      }

      const avg = this.calculateAverage()

      const values = this.values.map((val) => ({
        x: this.formatDateString(val.date),
        y: val.value
      }))

      const avgData =
        avg !== null
          ? this.values.map((val) => ({
              x: this.formatDateString(val.date),
              y: avg
            }))
          : []

      // Create expanded chart
      const config = {
        type: 'line',
        data: {
          datasets: [
            {
              label: 'Average',
              data: avgData,
              fill: false,
              borderDash: [3, 3],
              borderColor: '#aaa',
              borderWidth: 1
            },
            {
              label: this.label || 'Value',
              data: values,
              fill: 'origin',
              backgroundColor: (context) => {
                const chart = context.chart
                const { ctx, chartArea } = chart

                // This case happens on initial chart load
                if (!chartArea) return
                return this.createGradient(ctx, chartArea)
              },
              borderColor: '#6365f1',
              borderWidth: 2
            }
          ]
        },
        options: {
          responsive: true,
          animation: false,
          interaction: {
            mode: 'index',
            intersect: false
          },
          plugins: {
            legend: {
              display: true
            },
            tooltip: {
              enabled: true,
              callbacks: {
                label: (context) => {
                  const label = context.dataset.label || ''
                  const value = context.parsed.y
                  const decimals = value > 100 ? 0 : 2
                  return `${label}: ${formatMoney(value, decimals)}`
                }
              }
            }
          },
          elements: {
            point: {
              radius: 0,
              hoverRadius: 5 // Show points only on hover
            }
          },
          scales: {
            y: {
              beginAtZero: false,
              title: {
                display: true
              },
              ticks: {
                callback: (value) => {
                  // we want PSF values to have 2 decimals
                  // but larger values (such as effective rent) not to have decimals
                  const decimals = value > 100 ? 0 : 2
                  return formatMoney(value, decimals)
                }
              }
            },
            x: {
              title: {
                display: true,
                text: 'Date'
              }
            }
          }
        }
      } as ChartConfiguration<'line', DefaultDataPoint<'line'>>
      this.expandedChart = new Chart<'line'>(ctx, config)
    },
    renderChart() {
      const avg = this.calculateAverage()

      const ctx = this.$refs.sparkline as HTMLCanvasElement
      if (!ctx) {
        return
      }

      const values: { x: number; y: number }[] = []

      let lastVal = null
      const lastIdx = this.values.length - 1
      for (const [idx, val] of this.values.entries()) {
        const nextVal = idx !== lastIdx ? this.values[idx + 1] : null
        // we do not create a point if the next value is the same as the current value
        // the line chart will connect the first and last point of the same value automatically
        if (
          lastVal !== null &&
          val.value === lastVal &&
          nextVal !== null &&
          nextVal.value === lastVal
        ) {
          continue
        }
        values.push({ x: idx, y: val.value })
        lastVal = val.value
      }

      const avgData = [
        { x: 0, y: avg },
        { x: this.values.length - 1, y: avg }
      ]

      const currentChart = this.chart
      if (currentChart !== null) {
        currentChart?.destroy()
      }

      const config = {
        type: 'line',
        data: {
          datasets: [
            {
              data: avgData,
              fill: false,
              borderDash: [1, 1],
              borderColor: '#aaa',
              borderWidth: 1
            },
            {
              data: values,
              fill: 'origin',
              backgroundColor: (context) => {
                const chart = context.chart
                const { ctx, chartArea } = chart

                // This case happens on initial chart load
                if (!chartArea) return
                return this.createGradient(ctx, chartArea)
              },
              //backgroundColor: this.$q.dark.isActive ? '#1E1E26' : '#EFEFFD',
              borderWidth: 1.5
            }
          ]
        },
        options: {
          responsive: false,
          animation: false,
          parsing: false,
          events: [], // no hover bubbles
          plugins: {
            legend: {
              display: false
            },
            decimation: {
              enabled: true,
              samples: 50,
              algorithm: 'lttb'
            },
            tooltip: {
              enabled: false
            }
          },
          elements: {
            line: {
              borderColor: '#6365f1'
            },
            point: {
              radius: 0
            }
          },
          scales: {
            y: {
              type: 'linear',
              display: false
            },
            x: {
              type: 'linear',
              display: false,
              min: 0,
              max: this.values.length - 1
            }
          }
        }
      } as ChartConfiguration<'line', DefaultDataPoint<'line'>>

      this.chart = new Chart(ctx, config)
    }
  }
}
</script>

<style scoped>
.sparkline-container {
  width: 90px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
}
</style>
